import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { CalendarColors } from '../calendar_colors'

export default class extends Controller {
  static targets = ["calendar", "caret", "eventsTitle", "eventsContent", "eventFilter"]
  static values = { canManageEvents: Boolean }

  connect() {
    // Listen for DaisyUI dropdown events
    this.element.addEventListener('click', this.handleDropdownToggle.bind(this))
    
    // Listen for clicks outside to close dropdown (DaisyUI handles this, but we want caret animation)
    document.addEventListener('click', this.handleOutsideClick.bind(this))
    
    // Listen for filter changes
    this.eventFilterTargets.forEach(filter => {
      filter.addEventListener('change', this.handleFilterChange.bind(this))
    })
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
    if (this.calendar) {
      this.calendar.destroy()
    }
  }

  handleDropdownToggle(event) {
    // Check if the dropdown trigger was clicked
    if (event.target.closest('[role="button"]')) {
      // Small delay to allow DaisyUI to process the dropdown state
      setTimeout(() => {
        const dropdown = this.element.querySelector('.dropdown-content')
        const isOpen = dropdown && !dropdown.classList.contains('hidden') && dropdown.offsetParent !== null
        
        if (isOpen) {
          this.openDropdown()
        } else {
          this.closeDropdown()
        }
      }, 50)
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  openDropdown() {
    // Rotate caret
    if (this.hasCaretTarget) {
      this.caretTarget.style.transform = 'rotate(180deg)'
    }
    
    // Initialize FullCalendar if not already done
    if (!this.calendar && this.hasCalendarTarget) {
      this.initializeCalendar()
    }
  }

  closeDropdown() {
    // Reset caret rotation
    if (this.hasCaretTarget) {
      this.caretTarget.style.transform = 'rotate(0deg)'
    }
  }

  initializeCalendar() {
    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      headerToolbar: {
        start: 'title',
        center: '',
        end: 'prev,next'
      },
      height: 'auto',
      selectable: true,
      showNonCurrentDates: false,
      fixedWeekCount: false,

      dateClick: (info) => {
        // Handle date selection
        this.selectDate(info.dateStr)
      },
      datesSet: () => {
        // Replace icons when calendar navigates and add event indicators
        this.replaceNavigationIcons()
        this.addEventIndicators()
      },
      dayCellClassNames: (info) => {
        // Add custom classes for styling
        const today = new Date()
        const cellDate = new Date(info.date)
        
        if (cellDate.toDateString() === today.toDateString()) {
          return ['fc-today-custom']
        }
        return []
      }
    })
    
    this.calendar.render()
    
    // Wait longer and try multiple times to replace icons and add indicators
    setTimeout(() => {
      this.replaceNavigationIcons()
      this.addEventIndicators()
    }, 100)
    setTimeout(() => {
      this.replaceNavigationIcons()
      this.addEventIndicators()
    }, 300)
    setTimeout(() => {
      this.replaceNavigationIcons()
      this.addEventIndicators()
    }, 500)
    
    // Initialize events section
    this.initializeEventsSection()
  }

  async addEventIndicators() {
    // Get the current month view date
    const currentDate = this.calendar.getDate()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1 // JavaScript months are 0-indexed
    
    try {
      // Fetch events for the current month
      const response = await fetch(`/calendar/events?month=${month}&year=${year}`)
      const monthData = await response.json()
      
      // Clear previous event indicators
      this.calendarTarget.querySelectorAll('.fc-daygrid-day-number').forEach(dayNumber => {
        dayNumber.classList.remove('has-events', 'has-work', 'has-event', 'has-unavailability')
      })
      
      // Add indicators for days with events
      const dayNumbers = this.calendarTarget.querySelectorAll('.fc-daygrid-day-number')
      
      dayNumbers.forEach(dayNumber => {
        const dayText = dayNumber.textContent.trim()
        const dayNum = parseInt(dayText)
        const dayCell = dayNumber.closest('.fc-daygrid-day')
        
        if (dayCell && !dayCell.classList.contains('fc-day-other')) {
          // Check if this day has birthday events
          if (monthData.birthday_dates && monthData.birthday_dates.includes(dayNum)) {
            dayNumber.classList.add('has-events')
          }
          
          // Check if this day has work events
          if (monthData.work_dates && monthData.work_dates.includes(dayNum)) {
            dayNumber.classList.add('has-work')
          }
          
          // Check if this day has calendar events (by type)
          Object.keys(monthData).forEach(key => {
            if (key.endsWith('_dates') && (key === 'event_dates' || key === 'unavailability_dates')) {
              const eventType = key.replace('_dates', '')
              if (monthData[key] && monthData[key].includes(dayNum)) {
                dayNumber.classList.add(`has-${eventType}`)
              }
            }
          })
        }
      })
    } catch (error) {
      console.error('Failed to fetch event indicators:', error)
    }
  }

  replaceNavigationIcons() {
    // Small timeout to ensure DOM is ready
    setTimeout(() => {
      const prevButton = this.calendarTarget.querySelector('.fc-prev-button')
      const nextButton = this.calendarTarget.querySelector('.fc-next-button')
      
      if (prevButton) {
        // Clear existing content and add icon
        prevButton.innerHTML = ''
        const prevIcon = document.createElement('iconify-icon')
        prevIcon.setAttribute('icon', 'ph:caret-left')
        prevIcon.classList.add('text-lg')
        prevButton.appendChild(prevIcon)
        
        // Ensure button styling
        prevButton.style.display = 'flex'
        prevButton.style.alignItems = 'center'
        prevButton.style.justifyContent = 'center'
        prevButton.style.padding = '0'
        prevButton.style.minWidth = '2rem'
        prevButton.style.minHeight = '2rem'
      }
      
      if (nextButton) {
        // Clear existing content and add icon
        nextButton.innerHTML = ''
        const nextIcon = document.createElement('iconify-icon')
        nextIcon.setAttribute('icon', 'ph:caret-right')
        nextIcon.classList.add('text-lg')
        nextButton.appendChild(nextIcon)
        
        // Ensure button styling
        nextButton.style.display = 'flex'
        nextButton.style.alignItems = 'center'
        nextButton.style.justifyContent = 'center'
        nextButton.style.padding = '0'
        nextButton.style.minWidth = '2rem'
        nextButton.style.minHeight = '2rem'
      }
      

    }, 10)
  }

  selectDate(dateString) {
    // Remove previous selection from all day numbers
    this.calendarTarget.querySelectorAll('.fc-daygrid-day-number').forEach(dayNumber => {
      dayNumber.classList.remove('selected-date')
    })
    
    // Find the exact date cell using FullCalendar's data-date attribute
    const targetDayCell = this.calendarTarget.querySelector(`[data-date="${dateString}"]`)
    
    if (targetDayCell) {
      const dayNumber = targetDayCell.querySelector('.fc-daygrid-day-number')
      if (dayNumber) {
        dayNumber.classList.add('selected-date')
      }
    } else {
      // Fallback method: find by matching date string more precisely
      const clickDate = new Date(dateString + 'T00:00:00') // Ensure proper date parsing
      const dayNumber = clickDate.getDate().toString()
      const month = clickDate.getMonth()
      const year = clickDate.getFullYear()
      
      // Get current view date to ensure we're in the right month
      const currentDate = this.calendar.getDate()
      
      if (currentDate.getMonth() === month && currentDate.getFullYear() === year) {
        // Find day number in current month only
        const dayNumbers = this.calendarTarget.querySelectorAll('.fc-daygrid-day-number')
        
        dayNumbers.forEach(element => {
          if (element.textContent.trim() === dayNumber) {
            const dayCell = element.closest('.fc-daygrid-day')
            // Make sure it's in current month (not fc-day-other)
            if (dayCell && !dayCell.classList.contains('fc-day-other')) {
              element.classList.add('selected-date')
            }
          }
        })
      }
    }
    
    // Update events section title based on selected date
    this.updateEventsSection(dateString)
    
    // Emit custom event
    this.dispatch('dateSelected', { detail: { date: dateString } })
  }

  initializeEventsSection() {
    // Load events for today by default
    const today = new Date()
    const dateString = today.toISOString().split('T')[0] // Format: YYYY-MM-DD
    this.loadEventsForDate(dateString)
  }

  updateEventsSection(dateString) {
    this.loadEventsForDate(dateString)
  }

  async loadEventsForDate(dateString) {
    if (!this.hasEventsTitleTarget || !this.hasEventsContentTarget) return
    
    try {
      // Fetch events for the specific date
      const response = await fetch(`/calendar/events?date=${dateString}`)
      const data = await response.json()
      
      // Parse date string for title
      const [year, month, day] = dateString.split('-').map(Number)
      const selectedDate = new Date(year, month - 1, day)
      const today = new Date()
      
      // Reset time components for comparison
      const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      
      // Set title
      if (selectedDateOnly.getTime() === todayOnly.getTime()) {
        this.eventsTitleTarget.textContent = 'Today'
      } else {
        const options = { month: 'long', day: 'numeric' }
        this.eventsTitleTarget.textContent = selectedDate.toLocaleDateString('en-US', options)
      }
      
      // Display events
      if (data.events && data.events.length > 0) {
        const eventsHtml = data.events.map(event => {
          switch(event.type) {
            case 'birthday':
              return `<div>🎂 ${event.title}${event.age ? ` (${event.age} years old)` : ''}</div>`
            case 'work':
              const avatarContent = event.user_avatar 
                ? `<img src="${event.user_avatar}" alt="${event.user_first_name}" class="w-6 h-6 rounded-full object-cover">`
                : `<div class="w-6 h-6 rounded-full bg-primary text-primary-content flex items-center justify-center text-xs font-medium">${event.user_first_name.charAt(0)}</div>`
              
              const projectLinks = event.projects.map(project => 
                `<a href="/projects/${project.slug}" class="hover:text-primary hover:underline">${project.name}</a>`
              ).join(', ')
              
              return `<div class="flex items-center gap-2 mb-1">${avatarContent}<div class="min-w-0 flex-1"><div class="font-medium text-sm">${event.title}</div><div class="text-xs text-base-content/60">${projectLinks}</div></div></div>`
            case 'calendar_event':
              const eventTypeIcon = event.event_type === 'unavailability' ? '🚫' : '📅'
              
              let timeInfo = ''
              if (event.spans_multiple_days) {
                const startDate = new Date(event.start_date)
                const endDate = new Date(event.end_date)
                const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                
                // For unavailability events, include user names
                if (event.event_type === 'unavailability' && event.assigned_user_names && event.assigned_user_names.length > 0) {
                  const userNames = event.assigned_user_names.join(', ')
                  timeInfo = `${userNames}, ${startStr} - ${endStr}`
                } else {
                  timeInfo = `${startStr} - ${endStr}`
                }
              } else {
                // Single day event - show user names if available, then time
                const timeStr = new Date(event.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                if (event.assigned_user_names && event.assigned_user_names.length > 0) {
                  const userNames = event.assigned_user_names.join(', ')
                  timeInfo = `${userNames}, ${timeStr}`
                } else {
                  timeInfo = timeStr
                }
              }
              
              const deleteButton = this.canManageEventsValue ? 
                `<button class="btn btn-ghost btn-xs text-error opacity-0 group-hover:opacity-100 transition-opacity ml-2" 
                         data-action="click->calendar#deleteEvent" 
                         data-event-id="${event.id}" 
                         title="Delete event">
                   <iconify-icon icon="ph:trash"></iconify-icon>
                 </button>` : ''

              return `<div class="flex items-center gap-2 mb-1 group"><span class="text-lg">${eventTypeIcon}</span><div class="min-w-0 flex-1"><div class="font-medium text-sm truncate">${event.title}</div><div class="text-xs text-base-content/60 truncate">${timeInfo}</div></div>${deleteButton}</div>`
            default:
              return `<div>${event.title}</div>`
          }
        }).join('')
        
        this.eventsContentTarget.innerHTML = eventsHtml
      } else {
        this.eventsContentTarget.innerHTML = '<div class="text-base-content/60">Nothing on that day, how boring.</div>'
      }
    } catch (error) {
      console.error('Failed to load events:', error)
      this.eventsContentTarget.innerHTML = '<div class="text-base-content/60">Failed to load events</div>'
    }
  }

  handleFilterChange(event) {
    const eventType = event.target.dataset.eventType
    const isChecked = event.target.checked
    
    // Add or remove CSS class to control visibility
    if (isChecked) {
      this.element.classList.remove(`hide-${eventType}`)
    } else {
      this.element.classList.add(`hide-${eventType}`)
    }
  }

  // Method to delete a calendar event
  async deleteEvent(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const eventId = event.currentTarget.dataset.eventId
    
    if (!confirm('Are you sure you want to delete this event?')) {
      return
    }
    
    try {
      const response = await fetch(`/calendar_events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
      
      if (response.ok) {
        // Process the turbo_stream response
        const responseText = await response.text()
        Turbo.renderStreamMessage(responseText)
        
        // Also refresh the dropdown events for the current selected date
        const selectedDateElement = this.calendarTarget.querySelector('.selected-day')
        if (selectedDateElement) {
          const dateStr = selectedDateElement.dataset.date
          this.loadEventsForDate(dateStr)
        }
      } else {
        console.error('Failed to delete event')
        alert('Failed to delete event. Please try again.')
      }
    } catch (error) {
      console.error('Error deleting event:', error)
      alert('Error deleting event. Please try again.')
    }
  }
} 